import { CardStyleAsset } from "@1games-ru/region-shared/dist/types/bank/bank";
import { RefObject, createRef, forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/core/helpers/cn";
import { BankCard } from "@/domains/bank/components/BankCard/BankCard";
import { Tariff } from "@/domains/bank/types";

const borderColorMap = {
  standard: "border-sbank-classic",
  premium: "border-sbank-gold",
  platinum: "border-sbank-black",
} as const;

const CardButton = forwardRef<
  HTMLButtonElement,
  {
    variant: CardStyleAsset;
    isSelected: boolean;
    onMouseDown: () => void;
    name: string;
    withdrawalLimit: number;
    transferLimit: number;
    monthlyFee: number;
  }
>(({ variant, isSelected, onMouseDown, name, withdrawalLimit, transferLimit, monthlyFee }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        "flex-shrink-0 block p-1.5 border-2 border-transparent rounded-[22px] h-fit brightness-50 opacity-90 transition-all duration-300 ease-in-out cursor-pointer",
        isSelected && "brightness-110 opacity-100",
        isSelected && borderColorMap[variant],
      )}
      onMouseDown={onMouseDown}
    >
      <BankCard.Template tier={variant} name={name}>
        <BankCard.TierInfo
          freeWithdrawalLimit={withdrawalLimit}
          freeTransferLimit={transferLimit}
          monthlyFee={monthlyFee}
        />
      </BankCard.Template>
    </button>
  );
});

CardButton.displayName = "CardButton";

export function BankTariffSelect({
  tariffs,
  value,
  onChange,
}: {
  tariffs: Tariff[];
  value?: number;
  onChange: (id: number) => void;
}) {
  const [selectedIndex, setSelectedIndex] = useState<number>();
  const containerRef = useRef<HTMLDivElement>(null);

  const cardRefs = useRef<RefObject<HTMLButtonElement>[]>([]);

  const handleCardSelect = useCallback(
    (id: number, index: number) => {
      onChange(id);
      setSelectedIndex(index);

      const selectedCardRef = cardRefs.current[index];
      const container = containerRef.current;

      if (selectedCardRef?.current && container) {
        const cardElement = selectedCardRef.current;
        const containerRect = container.getBoundingClientRect();
        const cardRect = cardElement.getBoundingClientRect();

        const scrollLeft =
          cardElement.offsetLeft -
          container.offsetLeft -
          containerRect.width / 2 +
          cardRect.width / 2;

        container.scrollTo({
          left: scrollLeft,
          behavior: "smooth",
        });
      }
    },
    [onChange],
  );

  useEffect(() => {
    cardRefs.current = Array(tariffs.length)
      .fill(null)
      .map((_, i) => cardRefs.current[i] || createRef<HTMLButtonElement>());
  }, [tariffs.length]);

  // Sync selected index with value prop
  useEffect(() => {
    if (value !== undefined) {
      const index = tariffs.findIndex((t) => t.id === value);
      if (index !== -1) {
        setSelectedIndex(index);
      }
    }
  }, [value, tariffs]);

  // Select first by default if no value
  useEffect(() => {
    if (tariffs.length > 0 && selectedIndex === undefined && value === undefined) {
      handleCardSelect(tariffs[0].id, 0);
    }
  }, [tariffs.length, selectedIndex, value, handleCardSelect, tariffs]);

  return (
    <div className="relative">
      <div
        className={cn(
          "absolute z-1 left-0 top-0 bottom-0 w-12 h-full bg-gradient-to-r from-dark-900 to-transparent opacity-0 transition-opacity duration-300 ease-in-out pointer-events-none",
          selectedIndex !== 0 && "opacity-100",
        )}
      />

      <div
        ref={containerRef}
        className="flex gap-2 shrink-0 overflow-x-auto h-[240px] -mr-15 pr-15 scrollbar-appearance-none"
      >
        {tariffs.map((tariff, index) => (
          <CardButton
            key={tariff.id}
            ref={cardRefs.current[index]}
            variant={tariff.cardStyleAsset}
            name={tariff.name}
            withdrawalLimit={tariff.withdrawalLimit}
            transferLimit={tariff.transferLimit}
            monthlyFee={tariff.price}
            isSelected={selectedIndex === index}
            onMouseDown={() => handleCardSelect(tariff.id, index)}
          />
        ))}
      </div>

      <div className="absolute z-1 right-0 top-0 bottom-0 -mr-15 w-24 bg-gradient-to-r from-transparent to-dark-900 pointer-events-none" />
    </div>
  );
}
